import { saveUtilityCache } from '../../../utils/utilityAppStorage';
import { create } from 'zustand';
import { formatISO } from 'date-fns';

// Cargar dato inicial del localStorage
const getInitialWorkoutState = () => {
  try {
    const stored = localStorage.getItem('fitnessData');
    if (!stored) return { activeWorkout: null, history: [] };
    const data = JSON.parse(stored);
    return {
      activeWorkout: data.activeWorkout || null,
      history: data.history || [],
    };
  } catch (e) {
    console.error('Error loading workouts:', e);
    return { activeWorkout: null, history: [] };
  }
};

// Helper para guardar en localStorage unificado
const saveToLocalStorage = (state) => {
  try {
    const current = localStorage.getItem('fitnessData');
    const allData = current ? JSON.parse(current) : {};
    allData.activeWorkout = state.activeWorkout;
    allData.history = state.history;
    saveUtilityCache('fitnessData', allData);
  } catch (e) {
    console.error('Error saving workouts:', e);
  }
};

const initialState = getInitialWorkoutState();

export const useWorkoutStore = create((set, get) => ({
  activeWorkout: initialState.activeWorkout,
  history: initialState.history,

  startWorkout: (routineId, dayId) =>
    set((state) => {
      const newState = {
        ...state,
        activeWorkout: {
          id: Date.now().toString(),
          routineId,
          dayId,
          date: formatISO(new Date()),
          startTime: new Date(),
          exercises: [],
          notes: '',
        },
      };
      saveToLocalStorage(newState);
      return newState;
    }),

  addExerciseToWorkout: (exerciseData) =>
    set((state) => {
      if (!state.activeWorkout) return state;
      // Reuse the latest performed session, matching series by their original position.
      const previousExercise = [...state.history]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .flatMap((workout) => workout.exercises)
        .find((exercise) => exercise.exerciseId === exerciseData.exerciseId
          && exercise.sets.some((series) => series.completed));
      const newState = {
        ...state,
        activeWorkout: {
          ...state.activeWorkout,
          exercises: [
            ...state.activeWorkout.exercises,
            {
              exerciseId: exerciseData.exerciseId,
              exerciseName: exerciseData.exerciseName,
              sets: exerciseData.sets.map((s, index) => {
                const previous = previousExercise?.sets[index];
                return {
                  reps: previous?.completed ? (previous.reps ?? s.reps ?? 0) : (s.reps ?? 0),
                  weight: previous?.completed ? (previous.weight ?? s.weight ?? 0) : (s.weight ?? 0),
                  completed: false,
                };
              }),
            },
          ],
        },
      };
      saveToLocalStorage(newState);
      return newState;
    }),

  updateSetCompletion: (exerciseIndex, sets) =>
    set((state) => {
      if (!state.activeWorkout) return state;
      const exercises = [...state.activeWorkout.exercises];
      exercises[exerciseIndex].sets = sets;
      const newState = {
        ...state,
        activeWorkout: {
          ...state.activeWorkout,
          exercises,
        },
      };
      saveToLocalStorage(newState);
      return newState;
    }),

  updateWorkoutNotes: (notes) =>
    set((state) => {
      if (!state.activeWorkout) return state;
      const newState = {
        ...state,
        activeWorkout: {
          ...state.activeWorkout,
          notes,
        },
      };
      saveToLocalStorage(newState);
      return newState;
    }),

  finishWorkout: () =>
    set((state) => {
      if (!state.activeWorkout) return state;
      const completedWorkout = {
        ...state.activeWorkout,
        endTime: new Date(),
        duration: Math.round((new Date() - state.activeWorkout.startTime) / 60000),
      };
      const newState = {
        ...state,
        activeWorkout: null,
        history: [completedWorkout, ...state.history],
      };
      saveToLocalStorage(newState);
      return newState;
    }),

  cancelWorkout: () =>
    set((state) => {
      const newState = { ...state, activeWorkout: null };
      saveToLocalStorage(newState);
      return newState;
    }),

  getWorkoutHistory: () => get().history,

  getWorkoutsByExercise: (exerciseId) =>
    get().history.filter((w) => w.exercises.some((e) => e.exerciseId === exerciseId)),

  getExercisePersonalRecord: (exerciseId) => {
    const workouts = get().getWorkoutsByExercise(exerciseId);
    let maxWeight = 0;
    let maxVolume = 0;

    workouts.forEach((workout) => {
      const exercise = workout.exercises.find((e) => e.exerciseId === exerciseId);
      if (exercise) {
        exercise.sets.forEach((set) => {
          if (set.weight > maxWeight) maxWeight = set.weight;
          const volume = set.weight * set.reps;
          if (volume > maxVolume) maxVolume = volume;
        });
      }
    });

    return { maxWeight, maxVolume };
  },

  deleteWorkout: (workoutId) =>
    set((state) => {
      const newState = {
        ...state,
        history: state.history.filter((w) => w.id !== workoutId),
      };
      saveToLocalStorage(newState);
      return newState;
    }),
}));
